import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate, withLocalize } from 'react-localize-redux';
import { useFormikContext } from 'formik-2';
import { useSelector } from 'react-redux';
import Select from '../../../components/widgets/Select';
import { Button } from '../../../components/widgets';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import {
    ATTRIBUTION_TAB_IDS,
    TRANSLATE_PREFIX,
    getMacroAllowedPlatforms,
    isDefaultPostbackMacro,
    isPostbackMacroDisallowedForCell,
    trackingLinkKeyFromMacro,
} from '../../consts';
import { getPostbackMacroNetworkName } from '../../../selectors/partnerIntegration';
import useConfirmDialog from '../../useConfirmDialog';
import css from './CopyTemplatePanel.css';

const TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_TEMPLATES_TAB`;
const POSTBACK_TAB_PREFIX = `${TRANSLATE_PREFIX}.POSTBACK_OVERALL_SETUP_TAB`;
const PLATFORM_LABEL_PREFIX = `${TRANSLATE_PREFIX}.CAMPAIGN_TYPES.PLATFORM_OPTIONS`;
const TEMPLATES_TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES;
const OVERALL_TAB_ID = ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP;

const isTemplateNonEmpty = (rows, postbackMacroNetworkName) =>
    rows.some(
        r => !!r.partner_parameter_key?.trim() || !isDefaultPostbackMacro(r.singular_macro, postbackMacroNetworkName)
    );

/**
 * Filter source rows to those allowed on the target (template, event_type, platform) cell.
 * Mirrors `applyPostbackEventsPlatformsNext`'s new-combo seeding: drops macros forbidden by
 * `PLATFORM_TEMPLATE_DISALLOWED_MACROS_BY_EVENT_TYPE` (e.g. `{EVTNAME}` on HasOffers conversions)
 * AND macros not allowed on the target platform. Custom passthrough macros ({x?<network>}) are
 * platform-agnostic and preserved unless explicitly disallowed.
 * @param {Array<{singular_macro: string, partner_parameter_key: string}>} rows source template rows
 * @param {string} targetPlatform destination postback platform
 * @param {string} targetEventType destination event type
 * @param {?string} platformTemplate active platform template (e.g. 'hasoffers')
 * @returns {Array<{singular_macro: string, partner_parameter_key: string}>} rows allowed on the target cell
 */
export const filterRowsForTargetCell = (rows, targetPlatform, targetEventType, platformTemplate) =>
    (rows || []).filter(r => {
        if (isPostbackMacroDisallowedForCell(platformTemplate, targetEventType, r.singular_macro)) return false;
        if (trackingLinkKeyFromMacro(r.singular_macro)) return true;
        return getMacroAllowedPlatforms(r.singular_macro).includes(targetPlatform);
    });

const CopyTemplatePanel = ({ sourceEventType, sourcePlatform, readOnly = false, translate }) => {
    const { values, setFieldValue } = useFormikContext();
    const postbackMacroNetworkName = useSelector(getPostbackMacroNetworkName);
    const { confirm, dialog } = useConfirmDialog();
    const [selectedTarget, setSelectedTarget] = useState(null);

    const overallTab = values.formData?.[OVERALL_TAB_ID] || {};
    const templates = values.formData?.[TEMPLATES_TAB_ID] || {};
    const platformTemplate = values.formData?.[ATTRIBUTION_TAB_IDS.GENERAL]?.platform_template;
    const sourceRows = templates[sourceEventType]?.[sourcePlatform] || [];

    const targetOptions = useMemo(() => {
        const entries = overallTab.postback_events_and_platforms || [];
        return entries
            .flatMap(entry => (entry.platforms || []).map(platform => ({ eventType: entry.event_type, platform })))
            .filter(c => !(c.eventType === sourceEventType && c.platform === sourcePlatform))
            .map(c => {
                const eventLabel = translate(`${POSTBACK_TAB_PREFIX}.EVENT_TYPES.${c.eventType.toUpperCase()}`);
                const platformLabel = translate(`${PLATFORM_LABEL_PREFIX}.${c.platform.toUpperCase()}`);
                return {
                    value: `${c.eventType}|${c.platform}`,
                    label: `${eventLabel} — ${platformLabel}`,
                    eventType: c.eventType,
                    platform: c.platform,
                };
            });
    }, [overallTab.postback_events_and_platforms, sourceEventType, sourcePlatform, translate]);

    if (readOnly || targetOptions.length === 0) return null;

    const performCopy = (eventType, platform) => {
        setFieldValue(
            `formData.${TEMPLATES_TAB_ID}.${eventType}.${platform}`,
            filterRowsForTargetCell(sourceRows, platform, eventType, platformTemplate)
        );
        setSelectedTarget(null);
    };

    const handleApply = () => {
        if (!selectedTarget) return;
        const { eventType, platform } = selectedTarget;
        const targetRows = templates[eventType]?.[platform] || [];
        if (isTemplateNonEmpty(targetRows, postbackMacroNetworkName)) {
            confirm({
                titleKey: `${TAB_PREFIX}.CONFIRM_DIALOG.OVERWRITE_TEMPLATE_TITLE`,
                textKey: `${TAB_PREFIX}.CONFIRM_DIALOG.OVERWRITE_TEMPLATE_TEXT`,
                onConfirm: () => performCopy(eventType, platform),
            });
        } else {
            performCopy(eventType, platform);
        }
    };

    return (
        <>
            <div className={css.copyPanel}>
                <div className={css.copyInfo}>
                    <Icon name="copy" size={IconSizes.SMALL} variant={IconVariants.DARK} interactive={false} />
                    <div className={css.copyText}>
                        <span className={css.copyTitle}>
                            <Translate id={`${TAB_PREFIX}.COPY_TEMPLATE_TITLE`} />
                        </span>
                        <span className={css.copySubtitle}>
                            <Translate id={`${TAB_PREFIX}.COPY_TEMPLATE_SUBTITLE`} />
                        </span>
                    </div>
                </div>
                <div className={css.copyActions}>
                    <Select
                        options={targetOptions}
                        value={selectedTarget}
                        onChange={setSelectedTarget}
                        placeholder={translate(`${TAB_PREFIX}.COPY_TEMPLATE_PLACEHOLDER`)}
                        isClearable
                        isSearchable
                        className={css.copySelect}
                        usePortal
                    />
                    <Button
                        type="primary"
                        text={`${TAB_PREFIX}.COPY_TEMPLATE_BUTTON`}
                        onClick={handleApply}
                        disabled={!selectedTarget}
                    />
                </div>
            </div>
            {dialog}
        </>
    );
};

CopyTemplatePanel.propTypes = {
    sourceEventType: PropTypes.string.isRequired,
    sourcePlatform: PropTypes.string.isRequired,
    readOnly: PropTypes.bool,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(CopyTemplatePanel);
